// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { isPlanLimitError } from '@/account/utilities/is-plan-limit-error';
import { handleAPIError } from '@/api/error-utils';
import { Bundle } from '@/bundle/api/types';
import {
  ApplyBundleDialog,
  BundleApplyLine,
} from '@/bundle/components/apply-bundle-dialog';
import { buildBundleAppliedToast } from '@/bundle/utilities/bundle-applied-toast';
import { computePhysicalBundleApply } from '@/bundle/utilities/bundle-physical-apply';
import { DateShortcutButtons } from '@/components/date-shortcut-buttons';
import { FileDropZone, UploadingFile } from '@/components/file-drop-zone';
import { FormDatePicker } from '@/components/form-date-picker';
import { FormNumberTextField } from '@/components/form-number-text-field';
import { FormShippingCarrierSearch } from '@/components/form-shipping-carrier-search';
import { FormTextField } from '@/components/form-text-field';
import { Link } from '@/components/link';
import { SupportSnackbar } from '@/components/support-snackbar';
import {
  PROMISE_SHIP_DATE_SHORTCUTS,
  getPromiseDeliveryDateShortcuts,
} from '@/constants/date-shortcut';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  InfoOutlined,
  LibraryAddOutlined,
} from '@/constants/hardfin-standard-icons';
import {
  ShippingCarrier,
  shippingCarrierSchema,
  toTransportReferenceDetailsRequest,
} from '@/constants/shipping-carrier';
import { useToast } from '@/contexts/toast-context';
import { FormCustomerSearch } from '@/customer/components/form-customer-search';
import { getNewCompanyURL } from '@/customer/utilities/get-new-customer-url';
import { useDisclosure } from '@/hooks/use-disclosure';
import { ItemSearch } from '@/items/components/item-search';
import { FormLocationSearch } from '@/location/components/form-location-search';
import { getNewLocationURL } from '@/location/utilities/get-new-location-url';
import { ItemFormEntry, itemFormSchema } from '@/orders/schemas/item';
import { createPurchaseOrder } from '@/purchase-order/api/create-purchase-order';
import { PurchaseOrder } from '@/purchase-order/api/types';
import { updatePurchaseOrder } from '@/purchase-order/api/update-purchase-order';
import { isProcessed } from '@/purchase-order/constants/status';
import {
  purchaseOrderDetailQueryKey,
  purchaseOrdersQueryKey,
} from '@/purchase-order/query-keys';
import {
  SparseItem,
  SparseLocation,
  sparseCustomerSchema,
  sparseLocationSchema,
} from '@/sparse/api/types';
import { dateSchema } from '@/utilities/date-time-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

interface PurchaseOrderFormProps {
  /**
   * Optional purchase order to edit. If provided, the form will be in edit mode
   * with pre-populated values.
   */
  purchaseOrder?: PurchaseOrder;

  /**
   * Callback fired after successful form submission (create or update).
   * The dialog wrapper should use this to close the dialog.
   */
  onSubmit: () => void;

  /**
   * Callback fired when the mutation pending state changes.
   * Allows the parent dialog to read the loading state for button disabling.
   */
  onPendingChange: (isPending: boolean) => void;

  /**
   * The form ID used to connect the external submit button to this form.
   * The dialog wrapper provides this ID and uses it on its submit button.
   */
  formId: string;

  /**
   * Optional initial form data to pre-populate the form.
   * Used for cloning a purchase order with pre-filled values.
   */
  initialFormData?: PurchaseOrderFormData;

  /**
   * Optional file upload props for the file drop zone.
   * When provided, shows a file upload section next to the notes field.
   */
  fileUploadProps?: {
    files: UploadingFile[];
    onFilesSelected: (files: File[]) => void;
    onRemoveFile: (localId: string) => void;
    disabled?: boolean;
  };

  /**
   * Optional callback to get completed file IDs for the create request.
   * Should return an array of server file IDs for successfully uploaded files.
   */
  getFileIds?: () => string[];

  /**
   * The organization's default receive location, used to seed the to location
   * on new orders so users don't re-pick the primary warehouse every time. Only
   * applied on create when nothing else provides a destination; an archived
   * default is treated as unset.
   */
  orgDefaultReceiveLocation?: SparseLocation | null;
}

// The maximum number of line items a purchase order may carry
const PURCHASE_ORDER_MAX_ITEMS = 50;

// Extend the shared schema with item-level validation for PO
const poItemSchema = itemFormSchema.superRefine((data, ctx) => {
  if (data.item === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Item is required',
      path: ['item'],
    });
  }
  if (data.quantity === null || data.quantity <= 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Quantity must be greater than 0',
      path: ['quantity'],
    });
  }
});

/** Returns the transport reference details the form sends. */
function toFormTransportReferenceDetails(data: FormData) {
  return toTransportReferenceDetailsRequest(
    data.expectedTransportCarrier,
    data.expectedTransportCarrierName,
    data.expectedTransportReferenceNumber
  );
}

const formSchema = z
  .object({
    customer: sparseCustomerSchema.nullable(),
    externalPoNumber: z.string().nullable(),
    fromLocation: sparseLocationSchema.nullable(),
    toLocation: sparseLocationSchema.nullable(),
    promiseShipDate: dateSchema.nullable(),
    promiseDeliveryDate: dateSchema.nullable(),
    orderNotes: z.string().nullable(),
    expectedTransportCarrier: shippingCarrierSchema.nullable(),
    expectedTransportReferenceNumber: z.string().nullable(),
    expectedTransportCarrierName: z.string().nullable(),
    items: z
      .array(poItemSchema)
      .min(1, { message: 'At least one item is required' })
      .max(PURCHASE_ORDER_MAX_ITEMS),
  })
  .refine((data) => data.customer !== null, {
    message: 'Supplier is required',
    path: ['customer'],
  })
  .refine((data) => data.toLocation !== null, {
    message: 'To location is required',
    path: ['toLocation'],
  });

type FormData = z.infer<typeof formSchema>;

export type PurchaseOrderFormData = FormData;

const defaultValues: FormData = {
  customer: null,
  externalPoNumber: null,
  fromLocation: null,
  toLocation: null,
  promiseShipDate: null,
  promiseDeliveryDate: null,
  orderNotes: null,
  expectedTransportCarrier: null,
  expectedTransportReferenceNumber: null,
  expectedTransportCarrierName: null,
  items: [
    {
      item: null,
      quantity: 1,
    },
  ],
};

/**
 * Form component for creating or editing a purchase order.
 * This component manages form state and mutations, and can be embedded
 * in different dialog shells or tabbed interfaces.
 */
export function PurchaseOrderForm(props: PurchaseOrderFormProps) {
  const { onPendingChange } = props;
  const [showSnackbar, setShowSnackbar] = useState(false);
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const applyBundleDialog = useDisclosure();
  const isEditMode = !!props.purchaseOrder;

  const isPoProcessed = props.purchaseOrder?.status
    ? isProcessed(props.purchaseOrder.status)
    : false;

  const getDefaultValues = useCallback((): FormData => {
    // Use initialFormData if provided (for cloning a purchase order)
    if (props.initialFormData) {
      return props.initialFormData;
    }

    if (isEditMode && props.purchaseOrder) {
      return {
        customer: props.purchaseOrder.supplier,
        externalPoNumber:
          props.purchaseOrder.externalPurchaseOrderNumber ?? null,
        fromLocation: props.purchaseOrder.fromLocation,
        toLocation: props.purchaseOrder.toLocation,
        promiseShipDate: props.purchaseOrder.promiseShipDate,
        promiseDeliveryDate: props.purchaseOrder.promiseDeliveryDate,
        orderNotes: props.purchaseOrder.orderNotes,
        expectedTransportCarrier:
          props.purchaseOrder.transportReferenceDetails?.carrier ?? null,
        expectedTransportReferenceNumber:
          props.purchaseOrder.transportReferenceDetails?.transportReference ??
          null,
        expectedTransportCarrierName:
          props.purchaseOrder.transportReferenceDetails?.name ?? null,
        items: props.purchaseOrder.items.map((lineItem) => ({
          item: lineItem.item,
          quantity: lineItem.requestedQuantity,
        })),
      };
    }
    const baseValues = { ...defaultValues };
    if (
      props.orgDefaultReceiveLocation &&
      !props.orgDefaultReceiveLocation.isArchived
    ) {
      baseValues.toLocation = props.orgDefaultReceiveLocation;
    }
    return baseValues;
  }, [
    isEditMode,
    props.purchaseOrder,
    props.initialFormData,
    props.orgDefaultReceiveLocation,
  ]);

  const createMutation = useMutation({
    mutationFn: createPurchaseOrder,
    onSuccess: (purchaseOrder) => {
      // Invalidate all purchase order queries (list and details) and refetch active queries
      queryClient.invalidateQueries({
        queryKey: purchaseOrdersQueryKey,
        refetchType: 'active',
      });
      showToast({
        element: (
          <Typography>
            Purchase order{' '}
            <Link
              to="/purchase-orders/$purchaseOrderKey"
              params={{ purchaseOrderKey: purchaseOrder.key }}
              color="primary.main"
              sx={{ fontWeight: 'bold', textDecoration: 'underline' }}
            >
              {purchaseOrder.purchaseOrderNumber}
            </Link>{' '}
            created
          </Typography>
        ),
        severity: 'success',
        duration: 3000,
      });
      reset(defaultValues);
      props.onSubmit();
    },
    onError: (error) => {
      if (isPlanLimitError(error)) {
        return;
      }

      const apiError = handleAPIError(error);
      // Always show snackbar on error
      if (!apiError.sentToSentry) {
        captureException(error);
      }
      setShowSnackbar(true);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      purchaseOrderKey,
      request,
    }: {
      purchaseOrderKey: string;
      request: Parameters<typeof updatePurchaseOrder>[1];
    }) => updatePurchaseOrder(purchaseOrderKey, request),
    onSuccess: (updatedPurchaseOrder, variables) => {
      // Set the updated data directly in the cache for this specific purchase order
      queryClient.setQueryData(
        purchaseOrderDetailQueryKey(variables.purchaseOrderKey),
        updatedPurchaseOrder
      );
      // Invalidate all purchase order queries to ensure everything stays in sync
      queryClient.invalidateQueries({
        queryKey: purchaseOrdersQueryKey,
        refetchType: 'active',
      });
      reset(defaultValues);
      props.onSubmit();
    },
    onError: (error) => {
      const apiError = handleAPIError(error);
      // Always show snackbar on error
      if (!apiError.sentToSentry) {
        captureException(error);
      }
      setShowSnackbar(true);
    },
  });

  // Expose pending state to parent
  const isPending = createMutation.isPending || updateMutation.isPending;
  useEffect(() => {
    onPendingChange(isPending);
  }, [isPending, onPendingChange]);

  const { control, reset, handleSubmit, setValue, getValues } =
    useForm<FormData>({
      resolver: zodResolver(formSchema),
      defaultValues: getDefaultValues(),
    });

  const hasExistingTransportRef =
    !!props.purchaseOrder?.transportReferenceDetails?.carrier ||
    !!props.purchaseOrder?.transportReferenceDetails?.transportReference;
  const [isTransportRefOpen, setIsTransportRefOpen] = useState(
    hasExistingTransportRef
  );

  // Watch the promise ship date to anchor the delivery date shortcuts
  const selectedPromiseShipDate = useWatch({
    control,
    name: 'promiseShipDate',
  });

  const watchedCarrier = useWatch({
    control,
    name: 'expectedTransportCarrier',
  });
  const isOtherCarrier = watchedCarrier === ('other' as ShippingCarrier);

  const handleCloseTransportRef = () => {
    setValue('expectedTransportCarrier', null);
    setValue('expectedTransportReferenceNumber', null);
    setValue('expectedTransportCarrierName', null);
    setIsTransportRefOpen(false);
  };

  const currentItems = useWatch({
    control,
    name: 'items',
  });

  const {
    fields: itemsField,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'items',
  });

  const handleAddItem = () => {
    append({ item: null, quantity: 1 });
  };

  // Purchase orders forbid duplicate SKU rows and hold only physical items,
  // so applying a bundle sums each physical member's computed quantity into
  // an existing row for that SKU, appends members with no existing row (up to
  // the item cap), and drops service members.
  const handleApplyBundle = (lines: BundleApplyLine[], bundle: Bundle) => {
    const existingRows = getValues('items').map((row) => ({
      itemId: row.item?.id,
      quantity: row.quantity,
    }));
    const { updates, appends, fills, combined, appliedCount, skippedCount } =
      computePhysicalBundleApply(existingRows, lines, PURCHASE_ORDER_MAX_ITEMS);
    for (const update of updates) {
      setValue(`items.${update.index}.quantity`, update.quantity, {
        shouldValidate: true,
      });
    }
    for (const fill of fills) {
      setValue(`items.${fill.index}.item`, fill.line.item, {
        shouldValidate: true,
      });
      setValue(`items.${fill.index}.quantity`, fill.line.quantity, {
        shouldValidate: true,
      });
    }
    for (const appendLine of appends) {
      append({
        item: appendLine.item,
        quantity: appendLine.quantity,
      });
    }
    showToast({
      element: buildBundleAppliedToast({
        lineCount: appliedCount,
        bundleName: bundle.name,
        combined,
        skippedCount,
      }),
      severity: 'success',
    });
  };

  const onFormSubmit = (data: FormData) => {
    const items = data.items.map((entry) => ({
      itemId: entry.item?.id ?? '',
      requestedQuantity: entry.quantity ?? 0,
    }));

    if (isEditMode && props.purchaseOrder) {
      const updateRequest = {
        supplierId: data.customer?.id,
        externalPurchaseOrderNumber: data.externalPoNumber,
        fromLocationId: data.fromLocation?.id ?? null,
        toLocationId: data.toLocation?.id,
        promiseShipDate: data.promiseShipDate,
        promiseDeliveryDate: data.promiseDeliveryDate,
        orderNotes: data.orderNotes,
        transportReferenceDetails: toFormTransportReferenceDetails(data),
        items,
      };

      return updateMutation.mutate({
        purchaseOrderKey: props.purchaseOrder.key,
        request: updateRequest,
      });
    }

    const createRequest = {
      supplierId: data.customer?.id ?? '',
      externalPurchaseOrderNumber: data.externalPoNumber,
      fromLocationId: data.fromLocation?.id ?? null,
      toLocationId: data.toLocation?.id ?? '',
      promiseShipDate: data.promiseShipDate,
      promiseDeliveryDate: data.promiseDeliveryDate,
      orderNotes: data.orderNotes,
      transportReferenceDetails: toFormTransportReferenceDetails(data),
      items,
      fileIds: props.getFileIds?.() ?? null,
    };

    return createMutation.mutate(createRequest);
  };

  // Get list of selected items to hide from other selectors
  const selectedItems: SparseItem[] = currentItems
    .filter(
      (entry): entry is ItemFormEntry & { item: SparseItem } =>
        entry.item !== null
    )
    .map((entry) => entry.item);

  return (
    <>
      <form id={props.formId} onSubmit={handleSubmit(onFormSubmit)}>
        <Grid container spacing={2}>
          {/* Customer */}
          <Grid item xs={12} md={6}>
            <FormCustomerSearch
              name="customer"
              label="Supplier"
              control={control}
              filter={{ isArchived: 'all', isSupplier: true }}
              onCreate={(name: string) => {
                const url = getNewCompanyURL(name, false, true);
                window.open(url, '_blank');
              }}
            />
          </Grid>

          {/* External PO number */}
          <Grid item xs={12} md={6}>
            <FormTextField
              fullWidth
              name="externalPoNumber"
              label="External PO number"
              control={control}
              placeholder="Optional external reference"
            />
          </Grid>

          {/* From location */}
          <Grid item xs={12} md={6}>
            <FormLocationSearch
              name="fromLocation"
              control={control}
              label="From location (optional)"
              grouping={['inventory']}
              disabled={isPoProcessed}
              onCreate={(name: string) => {
                const url = getNewLocationURL(name);
                window.open(url, '_blank');
              }}
            />
          </Grid>

          {/* To location */}
          <Grid item xs={12} md={6}>
            <FormLocationSearch
              name="toLocation"
              control={control}
              label="To location"
              grouping={['inventory']}
              disabled={isPoProcessed}
              onCreate={(name: string) => {
                const url = getNewLocationURL(name);
                window.open(url, '_blank');
              }}
            />
          </Grid>

          {/* Promise ship date */}
          <Grid item xs={12} md={6}>
            <FormDatePicker
              name="promiseShipDate"
              label="Promise ship date"
              control={control}
              isClearable
              monthsToShow={2}
            />
            <DateShortcutButtons
              shortcuts={PROMISE_SHIP_DATE_SHORTCUTS}
              getAnchor={() => DateTime.now()}
              anchorName="today"
              onSelect={(date) =>
                setValue('promiseShipDate', date, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
            />
          </Grid>

          {/* Promise delivery date */}
          <Grid item xs={12} md={6}>
            <FormDatePicker
              name="promiseDeliveryDate"
              label="Promise delivery date"
              control={control}
              isClearable
              monthsToShow={2}
            />
            <DateShortcutButtons
              shortcuts={getPromiseDeliveryDateShortcuts(
                selectedPromiseShipDate !== null
              )}
              getAnchor={() => selectedPromiseShipDate ?? DateTime.now()}
              anchorName={
                selectedPromiseShipDate ? 'the promise ship date' : 'today'
              }
              onSelect={(date) =>
                setValue('promiseDeliveryDate', date, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
            />
          </Grid>

          {/* Transport reference */}
          <Grid item xs={12}>
            {!isTransportRefOpen ? (
              <Stack direction="row" justifyContent="flex-end">
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  onClick={() => setIsTransportRefOpen(true)}
                  sx={{ cursor: 'pointer' }}
                >
                  <Typography variant="body2" color="primary.main">
                    Add transport reference
                  </Typography>
                  <Tooltip
                    arrow
                    title="Optional master tracking or carrier reference for this purchase order"
                  >
                    <InfoOutlined
                      sx={{ fontSize: 16, color: 'text.secondary' }}
                    />
                  </Tooltip>
                </Stack>
              </Stack>
            ) : (
              <Box
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="subtitle2">
                      Transport reference
                    </Typography>
                    <Tooltip
                      arrow
                      title="Optional master tracking or carrier reference for this purchase order"
                    >
                      <InfoOutlined
                        sx={{ fontSize: 16, color: 'text.secondary' }}
                      />
                    </Tooltip>
                  </Stack>
                  <IconButton size="small" onClick={handleCloseTransportRef}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Stack>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormTextField
                      fullWidth
                      name="expectedTransportReferenceNumber"
                      label="Transport reference"
                      control={control}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormShippingCarrierSearch
                      name="expectedTransportCarrier"
                      label="Carrier"
                      control={control}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Collapse in={isOtherCarrier}>
                      <FormTextField
                        fullWidth
                        name="expectedTransportCarrierName"
                        label="Name"
                        control={control}
                      />
                    </Collapse>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Grid>

          {/* Purchase order notes */}
          <Grid item xs={12}>
            <FormTextField
              fullWidth
              name="orderNotes"
              label="Purchase order notes"
              control={control}
              isMultiline
              minimumRows={2}
              placeholder="Optional notes about this purchase order"
            />
          </Grid>
          {props.fileUploadProps && (
            <Grid item xs={12} md={12} sx={{ display: 'flex' }}>
              <FileDropZone
                files={props.fileUploadProps.files}
                onFilesSelected={props.fileUploadProps.onFilesSelected}
                onRemoveFile={props.fileUploadProps.onRemoveFile}
                disabled={props.fileUploadProps.disabled}
              />
            </Grid>
          )}

          {/* Items section */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="subtitle1">Purchase order items</Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddItem}
                  disabled={
                    itemsField.length >= PURCHASE_ORDER_MAX_ITEMS ||
                    isPoProcessed
                  }
                >
                  Add item
                </Button>
                {/* Stays enabled at the row cap: applying a bundle can still
                    auto-sum quantities into existing rows without adding rows */}
                <Button
                  variant="outlined"
                  startIcon={<LibraryAddOutlined />}
                  onClick={applyBundleDialog.open}
                  disabled={isPoProcessed}
                >
                  Add bundle
                </Button>
              </Stack>
            </Stack>

            {itemsField.map((field, index) => {
              const currentValue = currentItems[index];

              return (
                <Stack key={field.id} direction="row" spacing={2} mb={2}>
                  <Box sx={{ flex: 1 }}>
                    <Controller
                      control={control}
                      name={`items.${index}`}
                      render={({ field: itemField, fieldState }) => {
                        const itemError = (
                          fieldState.error as {
                            item?: { message?: string };
                          }
                        )?.item;

                        return (
                          <ItemSearch
                            label="Item"
                            value={currentValue?.item ?? null}
                            disabled={isPoProcessed}
                            physicalOnly={true}
                            onChange={(value) => {
                              itemField.onChange({
                                item: value,
                                quantity: currentValue?.quantity ?? 1,
                              });
                            }}
                            hiddenItems={selectedItems.filter(
                              (item) => item.id !== currentValue?.item?.id
                            )}
                            error={!!itemError}
                            helperText={itemError?.message}
                          />
                        );
                      }}
                    />
                  </Box>

                  <Box sx={{ width: '200px' }}>
                    <FormNumberTextField
                      name={`items.${index}.quantity`}
                      label="Quantity"
                      control={control}
                      numeric
                      disabled={isPoProcessed}
                    />
                  </Box>

                  <IconButton
                    onClick={() => remove(index)}
                    disabled={itemsField.length === 1 || isPoProcessed}
                    sx={{ alignSelf: 'center' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              );
            })}
          </Grid>
        </Grid>
      </form>
      <SupportSnackbar
        isOpen={showSnackbar}
        onClose={() => {
          setShowSnackbar(false);
          createMutation.reset();
          updateMutation.reset();
        }}
        message={
          isEditMode
            ? 'An error occurred updating the purchase order.'
            : 'An error occurred creating the purchase order.'
        }
      />
      <ApplyBundleDialog
        isOpen={applyBundleDialog.isOpen}
        onClose={applyBundleDialog.close}
        onApply={handleApplyBundle}
      />
    </>
  );
}
